<template>
  <div class="users-component">
    <h1 class="page-title">{{ $t('users.title_page') }}</h1>

    <select-entity
      v-if="showEntitySelection"
      :entities="entities"
      @entity-changed="onEntityChange"
      :preselectedEntityId="entityId"
    />

    <template v-if="showData">
      <div class="row no-print">
        <div class="col-12 col-md-6 p-b-15">
          <div class="input-group">
            <div class="input-group">
              <span class="input-group-addon">
                <i class="pg-search"></i>
              </span>
              <input @input="typingInput"
                    :placeholder="$t('users.search.placeholder')"
                    class="form-control search-users-input"
                    v-model="searchInput">
            </div>
          </div>
        </div>
        <div class="col-6 col-md-3 p-b-15">
          <span @click="addUserEvent">
            <router-link :to="{ name: 'NewUser', params: { entity_id: entityId }}" class="btn btn-secondary btn-block btn-cons">{{ $t("buttons.add") }}</router-link>
          </span>
        </div>
        <div class="col-6 col-md-3 p-b-15" v-if="hasUsers">
          <users-export-button @usersExportCSV="exportUsersFile('csv')"
                               @usersExportXLS="exportUsersFile('xls')"
                              :label="$t('users.export')" />
        </div>
      </div>

      <div>
        <ul class="nav nav-tabs nav-tabs-fillup hidden-sm-down" data-init-reponsive-tabs="dropdownfx">
          <li class="nav-item col-4">
            <a href="#" :class="{ active: isActiveTab(userStatus.active) }" @click="changeTab('active')" data-toggle="tab" data-target="#actived-users" aria-expanded="true">
              <span>{{ $t('users.active') }} ({{ usersData.active.total }})</span>
            </a>
          </li>
          <li class="nav-item col-4">
            <a href="#" :class="{ active: isActiveTab(userStatus.confirmed) }" @click="changeTab('confirmed')" data-toggle="tab" data-target="#confirmed-users" class="" aria-expanded="false">
              <span>{{ $t('users.confirmed') }} ({{ usersData.confirmed.total }})</span>
            </a>
          </li>
          <li class="nav-item col-4">
            <a href="#" :class="{ active: isActiveTab(userStatus.pending) }" @click="changeTab('pending')" data-toggle="tab" data-target="#pending-users" class="" aria-expanded="false">
              <span>{{ $t('users.pending') }} ({{ usersData.pending.total }})</span>
            </a>
          </li>
        </ul>
      </div>

      <div class="tab-content">
        <div class="tab-pane slide-left" :class="{ active: isActiveTab(userStatus.active) }" id="actived-users" aria-expanded="true">
          <users-table :users="usersData.active" :isManager="isManager" :entityId="entityId" :entities="entities" @sortChanged="sortChanged" status="active"></users-table>
        </div>
        <div class="tab-pane slide-left" :class="{ active: isActiveTab(userStatus.confirmed) }" id="confirmed-users" aria-expanded="false">
          <users-table :users="usersData.confirmed" :isManager="isManager" :entityId="entityId" :entities="entities" @sortChanged="sortChanged" status="confirmed"></users-table>
        </div>
        <div class="tab-pane slide-left" :class="{ active: isActiveTab(userStatus.pending) }" id="pending-users" aria-expanded="false" >
          <users-table :users="usersData.pending" :isManager="isManager" :entityId="entityId" :entities="entities" @sortChanged="sortChanged" status="pending"></users-table>
        </div>
      </div>

      <div class="d-flex justify-content-end">
        <a href="#" class="text-underline quickview-toggle" data-toggle-element="#quickview" data-toggle="quickview">{{ $t('users.link_add_candidate') }}</a>
      </div>
    </template>

    <CandidateSignUpFormSidebar v-if="entityId" :entityId="entityId" />

    <candidate-multi-action-popup
      v-if="showMultiActionPopup"
      :entityId="entityId"
      :isManager="isManager"
    />
  </div>
</template>

<script>
  import UsersTable from '@/components/users/UsersTable'
  import UsersExportButton from '@/components/users/UsersExportButton'
  import NewUser from '@/components/users/NewUser'
  import CandidateMultiActionPopup from './CandidateMultiActionPopup'
  import CandidateSignUpFormSidebar from '@/components/users/CandidateSignUpFormSidebar'
  import SelectEntity from '@/components/common/SelectEntity'
  import { userStatus } from '@/settings/users'
  import { mapActions, mapGetters } from 'vuex'
  import { getUserWithEntities } from '@/backend/users'
  import AtInternetManagingCandidate from '@/helpers/at_internet/managing_candidate'
  import _ from 'lodash'

  export default {
    name: 'Users',
    components: {
      UsersTable,
      NewUser,
      UsersExportButton,
      SelectEntity,
      CandidateMultiActionPopup,
      CandidateSignUpFormSidebar
    },
    data: function () {
      return {
        active: 'active',
        isManager: false,
        currentUser: {
          role: ''
        },
        entities: [],
        entityId: null,
        showData: true,
        searchInput: ''
      }
    },
    computed: {
      ...mapGetters({
        usersData: 'users/usersData',
        activeTab: 'users/activeTab',
        searchWord: 'users/searchWord'
      }),
      userStatus() {
        return userStatus
      },
      hasUsers() {
        var total = this.usersData.active.total +
                    this.usersData.confirmed.total +
                    this.usersData.pending.total;

        return total > 0
      },
      showEntitySelection() {
        if (this.currentUser && this.entities) {
          return (this.currentUser.role == 'recruiter' ||
                  this.currentUser.role == 'admin' ||
                  this.currentUser.role == 'corporation_admin') && this.entities.length > 1
        }

        return false;
      },
      showMultiActionPopup() {
        if (this.currentUser && this.entities) {
          return (this.currentUser.role == 'admin' ||
                  this.currentUser.role == 'corporation_admin') && this.entities.length > 1
        }
      }
    },
    methods: {
      ...mapActions({
        setSortOrder: 'users/setSortOrder',
        setSortField: 'users/setSortField',
        searchAllStates: 'users/searchAllStates',
        searchByText: 'users/searchByText',
        setActiveTab: 'users/setActiveTab'
      }),
      sortChanged: function(sortField, sortOrder){
        this.setSortOrder(sortOrder);
        this.setSortField(sortField);
        this.searchAllStates(this.entityId);
      },
      initData() {
        this.searchAllStates(this.entityId);
        this.searchInput = this.searchWord;
      },
      typingInput: _.debounce(function(e) {
        this.searchByText({ searchWord: e.target.value, entityId: this.entityId })
      }, 250),
      isActiveTab(tabName) {
        return tabName == this.activeTab
      },
      exportUsersFile(fileType) {
        AtInternetManagingCandidate.sendExportCandidatsEvent()
        this.$store.dispatch('users/download', {
          entity_id: this.entityId,
          is_manager: false,
          file_type: fileType
        })
      },
      async onEntityChange(entity) {
        await this.searchAllStates(entity.id);
        this.searchInput = this.searchWord;
        this.entityId = entity.id
        this.showData = true
      },
      changeTab(activeTab) {
        this.setActiveTab(activeTab)
        this.searchAllStates(this.entityId)
      },
      addUserEvent(){
        AtInternetManagingCandidate.sendAskAddCandidatEvent();
      }
    },
    mounted() {
      this.$store.commit('users/UPDATE_SEARCH_WORDS', '')

      if(this.$route && this.$route.params.entity_id) {
        this.entityId = this.$route.params.entity_id
      }

      getUserWithEntities().then(res => {
        const user = res.data
        this.currentUser = user
        this.entities = user.entities
        if (this.showEntitySelection) {
          if (this.entityId) {
            this.showData = true
            this.initData()
            this.reloadReponsiveTabs()
          } else {
            this.showData = false
          }
        } else {
          this.entityId = user.entities[0].id
          this.initData()
          this.reloadReponsiveTabs()
        }
      })
    },
    updated: function() {
      this.reloadReponsiveTabs();
    },
    destroyed() {
      this.$store.commit('users/SET_ENTITY_ID', null)
      this.$store.commit('users/SET_ACTIVE_TAB', 'active')
    }
  }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .text-underline {
    text-decoration: underline;
  }
</style>



// WEBPACK FOOTER //
// src/components/users/Users.vue